// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/SourcesPanel.test.tsx
import {
  Badge,
  Button,
  Flex,
  Popover,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useId, useState, type ReactNode } from "react";

import { KnowledgeScopeSource } from "../../../../../utils/parsers/knowledge";

export type ScopeValue = {
  brand?: string | null;
  region?: string | null;
  team?: string | null;
};

type ScopeEditPopoverProps = {
  scope: ScopeValue;
  scopeSource: KnowledgeScopeSource;
  onSave: (scope: {
    brand: string | null;
    region: string | null;
    team: string | null;
  }) => void;
  isSaving?: boolean;
  trigger: ReactNode;
};

/**
 * Shared brand/region/team scope editor (sources + rules). Free-text inputs —
 * scope dimensions are optional strings with no canonical vocabulary.
 * Inferred values are marked "suggested" until the first save, which stamps
 * scopeSource="user" server-side (the ingest latch then never overwrites).
 */
export function ScopeEditPopover({
  scope,
  scopeSource,
  onSave,
  isSaving = false,
  trigger,
}: ScopeEditPopoverProps) {
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(scope.brand ?? "");
  const [region, setRegion] = useState(scope.region ?? "");
  const [team, setTeam] = useState(scope.team ?? "");

  const brandId = useId();
  const regionId = useId();
  const teamId = useId();

  function handleOpenChange(next: boolean) {
    if (next) {
      // Re-prefill from the latest scope every open.
      setBrand(scope.brand ?? "");
      setRegion(scope.region ?? "");
      setTeam(scope.team ?? "");
    }
    setOpen(next);
  }

  function handleSave() {
    onSave({
      brand: brand.trim() || null,
      region: region.trim() || null,
      team: team.trim() || null,
    });
    setOpen(false);
  }

  const isInferred = scopeSource === KnowledgeScopeSource.INFERRED;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>{trigger}</Popover.Trigger>
      <Popover.Content
        className="max-h-[min(70vh,420px)] w-72 overflow-y-auto"
        aria-label="Edit scope"
        data-testid="knowledge-scope-popover"
      >
        <Flex direction="column" gap="2">
          {isInferred ? (
            <Badge
              color="amber"
              variant="soft"
              data-testid="knowledge-scope-suggested-badge"
            >
              Suggested by AI — save to confirm
            </Badge>
          ) : null}
          <label htmlFor={brandId}>
            <Text size="1" color="gray">
              Brand
            </Text>
          </label>
          <TextField.Root
            id={brandId}
            size="1"
            value={brand}
            placeholder="e.g. Activia"
            onChange={(e) => setBrand(e.target.value)}
            data-testid="knowledge-scope-input-brand"
          />
          <label htmlFor={regionId}>
            <Text size="1" color="gray">
              Region
            </Text>
          </label>
          <TextField.Root
            id={regionId}
            size="1"
            value={region}
            placeholder="e.g. EMEA"
            onChange={(e) => setRegion(e.target.value)}
            data-testid="knowledge-scope-input-region"
          />
          <label htmlFor={teamId}>
            <Text size="1" color="gray">
              Team
            </Text>
          </label>
          <TextField.Root
            id={teamId}
            size="1"
            value={team}
            placeholder="e.g. Design"
            onChange={(e) => setTeam(e.target.value)}
            data-testid="knowledge-scope-input-team"
          />
          <Button
            size="1"
            onClick={handleSave}
            loading={isSaving}
            data-testid="knowledge-scope-save"
          >
            Save scope
          </Button>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}

/** Compact read-only scope chips shared by the sources + rules tables. */
export function ScopeChips({
  scope,
  testId,
}: {
  scope: ScopeValue;
  testId: string;
}) {
  const chips = [
    scope.brand ? { key: "brand", value: scope.brand } : null,
    scope.region ? { key: "region", value: scope.region } : null,
    scope.team ? { key: "team", value: scope.team } : null,
  ].filter(Boolean) as { key: string; value: string }[];

  if (chips.length === 0) {
    return (
      <Text size="1" color="gray" data-testid={testId}>
        —
      </Text>
    );
  }
  return (
    <Flex gap="1" wrap="wrap" data-testid={testId}>
      {chips.map((chip) => (
        <Badge key={chip.key} color="gray" variant="soft">
          {chip.value}
        </Badge>
      ))}
    </Flex>
  );
}
